import { createWithEqualityFn as create } from 'zustand/traditional';
import type { CartDto, CartDtoVatTypeEnum, DiscountDto } from '../../../generated-api';
import { clearCartFromStorage, createEmptyCart, getCartFromStorage } from './CartStorage';
import { calculateTotalCartPrice, calculateTotalCartTakeawayPrice } from './ClientCartMapper';
import type { CompositeSplitData, CompositeUpdateData } from './CartLogic';
import {
  handleAddItem,
  handleDelete,
  handleSetOrderLineComment,
  handleSplitCompositeProduct,
  handleUpdateCompositeProduct,
} from './CartLogic';
import type { AddItemDto, AddItemsDto, ClientCartDto, EditItemWrapper } from '../../../types/cart';
import type { BundleDefinition, BundleMatch } from '../../utils/bundleDetection';
import { detectBundles } from '../../utils/bundleDetection';

export interface CartStoreState {
  cart: ClientCartDto;
  appliedDiscount?: DiscountDto;
  giftCardPhoneNumber?: string;
  timestampFirstItemAdded?: Date;

  // Bundle detection
  bundleDefinitions: BundleDefinition[];
  pendingBundlePrompt?: BundleMatch[];
  dismissedBundleVariantIds: string[];

  // External dependencies
  locationId: string;
  storage?: Storage;
  discounts?: DiscountDto[];

  init: (locationId: string, discounts?: DiscountDto[], storage?: Storage, defaultVatType?: CartDtoVatTypeEnum) => void;

  // Actions
  addMenuItem: (item: AddItemDto, quantity?: number) => void;
  addMenuItems: (items: AddItemsDto) => void;
  deleteMenuItems: (items: AddItemsDto) => void;
  editCartItem: (edit: EditItemWrapper) => void;
  moveCourse: (edit: EditItemWrapper) => void;
  applyDiscount: (discount?: DiscountDto) => void;
  setGiftCardPhoneNumber: (phone?: string) => void;
  saveComment: (comment?: string) => void;
  saveVatType: (vatType: CartDtoVatTypeEnum) => void;
  getCartSnapshot: () => ClientCartDto;
  getPersistedCart: () => ClientCartDto;
  resetCart: () => ClientCartDto;
  overrideCart: (cart: CartDto) => void;
  restoreCart: (dto: CartDto) => void;
  setOrderLineComment: (orderLineKey: string, comment?: string) => void;
  updateCompositeProduct: (update: CompositeUpdateData) => void;
  splitCompositeProduct: (data: CompositeSplitData) => void;
  setBundleDefinitions: (defs: BundleDefinition[]) => void;
  dismissBundlePrompt: () => void;
  convertToBundle: (deleteItems: AddItemsDto[], addBundle: AddItemsDto) => void;
  getOriginalTotalPrice: () => {
    totalPrice: number;
    totalTakeawayPrice: number;
  };
}

export const useCartStore = create<CartStoreState>()((set, get) => ({
  addMenuItem: (item, quantity = 1) => {
    set((state) => {
      const newCart = handleAddItem(state.cart, item, quantity);
      const appliedDiscount = state.appliedDiscount;

      const allMatches =
        state.bundleDefinitions.length > 0 ? detectBundles(newCart.orderLines, state.bundleDefinitions) : [];
      const matches = allMatches.filter((m) => !state.dismissedBundleVariantIds.includes(m.bundle.variantId));

      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, appliedDiscount),
        },
        pendingBundlePrompt: matches.length > 0 ? matches : undefined,
        timestampFirstItemAdded: state.cart.orderLines.length === 0 ? new Date() : state.timestampFirstItemAdded,
      };
    });
  },
  addMenuItems: ({ item, quantity }) => get().addMenuItem(item, quantity),
  appliedDiscount: undefined,
  applyDiscount: (discount) => {
    set((state) => ({
      appliedDiscount: discount,
      cart: {
        ...state.cart,
        discountId: discount?.id,
        totalPrice: calculateTotalCartPrice(state.cart.orderLines, discount),
        totalTakeawayPrice: calculateTotalCartTakeawayPrice(state.cart.orderLines, discount),
      },
    }));
  },
  bundleDefinitions: [],
  cart: createEmptyCart(''),
  convertToBundle: (deleteItems, addBundle) => {
    set((state) => {
      let cart = state.cart;
      for (const del of deleteItems) {
        cart = handleDelete(cart, del);
      }
      const newCart = handleAddItem(cart, addBundle.item, addBundle.quantity);
      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, state.appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, state.appliedDiscount),
        },
        pendingBundlePrompt: undefined,
      };
    });
  },
  deleteMenuItems: (items) => {
    set((state) => {
      const newCart = handleDelete(state.cart, items);
      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, state.appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, state.appliedDiscount),
        },
      };
    });
  },
  discounts: undefined,
  dismissBundlePrompt: () => {
    const { pendingBundlePrompt, dismissedBundleVariantIds } = get();
    const newDismissed = pendingBundlePrompt
      ? [...dismissedBundleVariantIds, ...pendingBundlePrompt.map((m) => m.bundle.variantId)]
      : dismissedBundleVariantIds;
    set({ pendingBundlePrompt: undefined, dismissedBundleVariantIds: newDismissed });
  },
  dismissedBundleVariantIds: [],
  editCartItem: (edit) => {
    set((state) => {
      const stateAfterDelete = handleDelete(state.cart, edit.deleteItemsDto);
      const newCart = handleAddItem(stateAfterDelete, edit.addItemsDto.item, edit.addItemsDto.quantity);
      // const newCart = handleEditCartItem(state.cart, edit);
      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, state.appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, state.appliedDiscount),
        },
      };
    });
  },
  getCartSnapshot: () => get().cart,
  getOriginalTotalPrice: () => {
    const { cart } = get();
    return {
      totalPrice: calculateTotalCartPrice(cart.orderLines, undefined),
      totalTakeawayPrice: calculateTotalCartTakeawayPrice(cart.orderLines, undefined),
    };
  },
  getPersistedCart: () => {
    const { cart, locationId, storage } = get();
    const persistedCart = getCartFromStorage(locationId, storage);
    return persistedCart ?? cart;
  },
  giftCardPhoneNumber: undefined,
  init: (locationId, discounts, storage, defaultVatType) => {
    set((state) => {
      const cart = getCartFromStorage(locationId, storage, defaultVatType);
      const appliedDiscount = discounts?.find((d) => d.id === cart.discountId);

      const updatedState = {
        ...state,
        cart,
        locationId,
        storage,
        discounts,
        appliedDiscount,
      };

      return updatedState;
    });
  },
  locationId: '',
  moveCourse: ({ deleteItemsDto, addItemsDto }) => {
    const state = get();
    const cartWithDeleted = handleDelete(state.cart, deleteItemsDto);
    const cartWithAdded = handleAddItem(cartWithDeleted, addItemsDto.item, addItemsDto.quantity);
    set({
      cart: {
        ...cartWithAdded,
        totalPrice: calculateTotalCartPrice(cartWithAdded.orderLines, state.appliedDiscount),
        totalTakeawayPrice: calculateTotalCartTakeawayPrice(cartWithAdded.orderLines, state.appliedDiscount),
      },
    });
  },
  overrideCart: (cart) => {
    set((state) => ({
      cart: {
        ...createEmptyCart(state.locationId),
        ...cart,
      },
    }));
  },
  pendingBundlePrompt: undefined,
  resetCart: () => {
    const { locationId, storage } = get();
    const emptyCart = createEmptyCart(locationId);
    clearCartFromStorage(locationId, storage);
    set({
      cart: emptyCart,
      appliedDiscount: undefined,
      giftCardPhoneNumber: undefined,
      timestampFirstItemAdded: undefined,
      pendingBundlePrompt: undefined,
      dismissedBundleVariantIds: [],
    });
    return emptyCart;
  },
  restoreCart: (restoreDto) => {
    const { discounts } = get();
    const discount = restoreDto.discountId ? discounts?.find((d) => d.id === restoreDto.discountId) : undefined;

    set({
      cart: {
        ...restoreDto,
        totalPrice: calculateTotalCartPrice(restoreDto.orderLines, discount),
        totalTakeawayPrice: calculateTotalCartTakeawayPrice(restoreDto.orderLines, discount),
      },
      appliedDiscount: discount,
    });
  },
  saveComment: (comment) => {
    set((state) => ({ cart: { ...state.cart, comment } }));
  },
  saveVatType: (vatType) => {
    set((state) => ({ cart: { ...state.cart, vatType } }));
  },
  setBundleDefinitions: (defs) => set({ bundleDefinitions: defs }),
  setGiftCardPhoneNumber: (phone) => set({ giftCardPhoneNumber: phone }),
  setOrderLineComment: (orderLineKey, comment) => {
    set((state) => ({
      cart: handleSetOrderLineComment(orderLineKey, state.cart, comment),
    }));
  },
  splitCompositeProduct: (data) => {
    set((state) => {
      const newCart = handleSplitCompositeProduct(state.cart, data);
      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, state.appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, state.appliedDiscount),
        },
      };
    });
  },
  storage: undefined,
  timestampFirstItemAdded: undefined,
  updateCompositeProduct: (update) => {
    set((state) => {
      const newCart = handleUpdateCompositeProduct(state.cart, update);
      return {
        cart: {
          ...newCart,
          totalPrice: calculateTotalCartPrice(newCart.orderLines, state.appliedDiscount),
          totalTakeawayPrice: calculateTotalCartTakeawayPrice(newCart.orderLines, state.appliedDiscount),
        },
      };
    });
  },
}));
